import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { TRAITS } from '../data/misc';
import { BROOD_DURATION_SEC } from '../data/balance';
import { buyNestBox, collectNest, crow, releaseHen } from '../core/actions';
import { broodyChoice } from '../core/broody';
import { clickCritter, critterReward } from '../core/critters';
import { crowStats, henStats, nestBoxCost, nestCapacity } from '../core/economy';
import { collectEvent } from '../core/events';
import { formatDuration, formatMoney, formatRate } from '../core/format';
import { isFirstOfKind } from '../core/modifiers';
import { HEN_SPECIALS, ROOSTER_PASSIVES } from '../core/specials';
import type { Critter, GameEvent } from '../core/types';
import { computeLayout, boxRect, fieldPoint, roosterRect, type FarmLayout } from '../render/layout';
import { drawFarm, hitTest, type HitTarget, type RenderFx } from '../render/farm';
import { crowRing, floatText, popRing, sparkles } from '../render/particles';
import { clickCluck, clickCrow, clickRoosterCluck, clickSfx } from '../platform/audio';
import { game } from './controller';
import { byId, h } from './dom';
import { confirmModal } from './modals';
import { toast } from './toasts';

let canvas: HTMLCanvasElement;
let ctx2d: CanvasRenderingContext2D;
let layout: FarmLayout = computeLayout(800, 600);
const fx: RenderFx = { bounce: new Map(), hover: null, theme: 'twilight' };
let tooltip: HTMLElement;
let popover: HTMLElement | null = null;
let onPlaceRequest: (boxIndex: number) => void = () => {};

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export function initFarmView(placeRequest: (boxIndex: number) => void): void {
  onPlaceRequest = placeRequest;
  canvas = byId<HTMLCanvasElement>('farm-canvas');
  ctx2d = canvas.getContext('2d')!;
  tooltip = byId('tooltip');
  const host = byId('farm');
  new ResizeObserver(() => resize(host)).observe(host);
  resize(host);
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', () => { fx.hover = null; tooltip.style.display = 'none'; });
  canvas.addEventListener('contextmenu', onContextMenu);
  document.addEventListener('pointerdown', (e) => {
    if (popover && !popover.contains(e.target as Node)) closePopover();
  });
}

function resize(host: HTMLElement): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(280, host.clientWidth);
  const hgt = Math.max(300, host.clientHeight);
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(hgt * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${hgt}px`;
  ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
  layout = computeLayout(w, hgt);
}

export function renderFarm(now: number): void {
  fx.theme = document.documentElement.dataset.theme ?? 'twilight';
  ctx2d.clearRect(0, 0, layout.width, layout.height);
  drawFarm(ctx2d, layout, game.state, game.viewFlock(), game.simNow(), now, fx);
}

/** Floating text at a normalized field point of a flock (only shown if that flock is in view). */
export function fieldFx(flockId: number, nx: number, ny: number, text: string, color = '#ffd54a'): void {
  if (flockId !== game.viewFlock().id) return;
  const p = fieldPoint(layout, nx, ny);
  floatText(p.x, p.y - 10, text, color, performance.now(), Math.max(14, layout.font * 1.1));
  sparkles(p.x, p.y, color, performance.now(), 6);
}

function localPoint(e: PointerEvent | MouseEvent): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

function target(e: PointerEvent | MouseEvent): HitTarget | null {
  const p = localPoint(e);
  return hitTest(layout, game.state, game.viewFlock(), p.x, p.y, game.simNow());
}

// ── Clicks ────────────────────────────────────────────────────────────

const EVENT_SOUND: Record<GameEvent['kind'], Parameters<typeof clickSfx>[0]> = {
  yolkBurst: 'yolk_burst', eggShower: 'egg_shower', doubleTime: 'double_time', silver: 'golden_egg', golden: 'golden_egg',
  rotten: 'rotten_egg', rainbow: 'golden_egg', roosterEgg: 'golden_egg', broody: 'broody_coo', broodEgg: 'matriarch_promote',
};

function onPointerDown(e: PointerEvent): void {
  if (e.button !== 0) return;
  const t = target(e);
  const s = game.state;
  const c = game.ctx();
  const flock = game.viewFlock();
  const p = localPoint(e);
  const now = performance.now();
  if (!t) return;
  switch (t.kind) {
    case 'event': {
      const ev = s.events.find((x) => x.id === t.id);
      if (!ev) return;
      const r = collectEvent(s, c, t.id);
      if (!r.ok) return;
      clickSfx(EVENT_SOUND[ev.kind]);
      popRing(p.x, p.y, '#ffe27a', now);
      if (r.amount > 0) floatText(p.x, p.y - 12, `+${formatMoney(r.amount)}`, '#ffd54a', now, layout.font * 1.3);
      if (r.detail && (ev.kind !== 'broodEgg')) toast(r.detail, ev.kind === 'rotten' ? 'info' : 'good');
      game.changed();
      break;
    }
    case 'critter': {
      const cr = s.critters.find((x) => x.id === t.id);
      if (!cr) return;
      const r = clickCritter(s, c, t.id);
      if (!r.ok) return;
      clickSfx(cr.kind === 'worm' || cr.kind === 'megaWorm' ? 'worm_click' : cr.kind === 'butterfly' ? 'butterfly_click' : 'bonus_click');
      if (r.amount > 0) floatText(p.x, p.y - 10, `+${formatMoney(r.amount)}`, '#ffd54a', now);
      else if (r.detail) floatText(p.x, p.y - 10, r.detail, '#ffb3e6', now, layout.font);
      break;
    }
    case 'rooster': {
      const rooster = flock.rooster!;
      const r = crow(s, c, flock.id);
      const rr = roosterRect(layout);
      if (r.ok) {
        clickCrow(rooster.breed);
        crowRing(rr.x + rr.w / 2, rr.y + rr.h / 2, now);
        floatText(rr.x + rr.w / 2, rr.y, `Cock-a-doodle-doo! ${r.detail ?? ''}`, '#ffcf40', now, layout.font * 1.1);
      } else {
        clickRoosterCluck(rooster.breed);
        floatText(rr.x + rr.w / 2, rr.y + 10, `Next crow in ${formatDuration(rooster.crowReadyAt - s.time)}`, '#cfd8e3', now, layout.font);
      }
      break;
    }
    case 'box': {
      const box = flock.boxes[t.index];
      const rect = boxRect(layout, t.index);
      if (!box) {
        if (t.index !== flock.boxes.length) return;
        const cost = nestBoxCost(s, flock.id);
        const r = buyNestBox(s, flock.id);
        if (r.ok) {
          clickSfx('purchase');
          floatText(rect.x + rect.w / 2, rect.y + rect.h / 2, 'New nesting box!', '#9fe07f', now);
          game.changed();
          onPlaceRequest(flock.boxes.length - 1);
        } else {
          floatText(rect.x + rect.w / 2, rect.y + rect.h / 2, `Need ${formatMoney(cost)}`, '#ff9a8a', now, layout.font);
        }
        return;
      }
      if (!box.hen) {
        onPlaceRequest(t.index);
        return;
      }
      const broody = s.events.find((x) => x.kind === 'broody' && x.flockId === flock.id && x.boxIndex === t.index);
      if (broody && !broody.broodUntil) {
        openBroodyPopover(broody, e.clientX, e.clientY);
        return;
      }
      const r = collectNest(s, c, flock.id, t.index);
      if (r.ok) {
        clickSfx('egg_click');
        fx.bounce.set(t.index, now);
        popRing(rect.x + rect.w / 2, rect.y + rect.h * 0.74, '#fff1b8', now);
        floatText(rect.x + rect.w / 2, rect.y + rect.h * 0.35, `+${formatMoney(r.amount)}`, '#ffd54a', now, layout.font * 1.2);
        if (r.detail?.endsWith('egg!')) floatText(rect.x + rect.w / 2, rect.y + rect.h * 0.15, r.detail, '#e0a878', now, layout.font);
      } else {
        clickCluck(box.hen.breed);
        fx.bounce.set(t.index, now);
      }
      break;
    }
  }
}

function onContextMenu(e: MouseEvent): void {
  const t = target(e);
  if (!t || t.kind !== 'box') return;
  const flock = game.viewFlock();
  const hen = flock.boxes[t.index]?.hen;
  if (!hen) return;
  e.preventDefault();
  confirmModal('Release this hen?', `${HEN_BREEDS[hen.breed].name} will go live on a nicer farm. You won't get a refund.`, 'Release', () => {
    releaseHen(game.state, flock.id, t.index);
    game.changed();
  }, true);
}

// ── Broody popover ────────────────────────────────────────────────────

function closePopover(): void {
  popover?.remove();
  popover = null;
}

function openBroodyPopover(ev: GameEvent, x: number, y: number): void {
  closePopover();
  const flock = game.viewFlock();
  const hen = flock.boxes[ev.boxIndex ?? -1]?.hen;
  if (!hen) return;
  const left = Math.max(0, Math.ceil(((ev.decideBy ?? 0) - game.state.time) / 1000));
  popover = h('div', { class: 'popover' },
    h('strong', { text: `${HEN_BREEDS[hen.breed].name} is broody 🪺` }),
    h('p', { class: 'hint', text: `Let her sit ${Math.round(BROOD_DURATION_SEC / 60)} minutes to hatch a brood egg and maybe earn a matriarch trait — she won't lay meanwhile. Decides for you in ${left}s.` }),
    h('div', { class: 'row' },
      h('button', { class: 'btn small', type: 'button', onclick: () => { clickSfx('broody_coo'); broodyChoice(game.state, game.ctx(), ev.id, 'brood'); game.changed(); closePopover(); } }, 'Let her brood'),
      h('button', { class: 'btn small secondary', type: 'button', onclick: () => { clickSfx('hen_cluck'); broodyChoice(game.state, game.ctx(), ev.id, 'shoo'); game.changed(); closePopover(); } }, 'Shoo!'),
    ),
  );
  document.body.append(popover);
  popover.style.left = `${Math.min(window.innerWidth - 250, x + 8)}px`;
  popover.style.top = `${Math.min(window.innerHeight - 160, y + 8)}px`;
}

// ── Tooltips ──────────────────────────────────────────────────────────

function onPointerMove(e: PointerEvent): void {
  const t = target(e);
  fx.hover = t;
  const html = t ? tooltipHtml(t) : null;
  if (!html || e.pointerType === 'touch') {
    tooltip.style.display = 'none';
    return;
  }
  tooltip.innerHTML = html;
  tooltip.style.display = 'block';
  const pad = 14;
  const w = tooltip.offsetWidth;
  const hh = tooltip.offsetHeight;
  tooltip.style.left = `${Math.min(window.innerWidth - w - 8, e.clientX + pad)}px`;
  tooltip.style.top = `${Math.min(window.innerHeight - hh - 8, e.clientY + pad)}px`;
}

const EVENT_HELP: Record<GameEvent['kind'], string> = {
  yolkBurst: 'Collects every egg in every nest at double value.',
  eggShower: 'Rains a handful of golden storm eggs — click them!',
  doubleTime: 'Hens lay twice as fast for 30 seconds.',
  silver: 'Pays out a chunk of income. Click it while a golden egg is out for a Silver Storm!',
  golden: 'A surprise: Egg Burst, Laying Frenzy, Lucky Clutch, or Egg Storm.',
  rotten: 'Click to compost it for a little cash. Ignore it and egg value drops 15% for 30s.',
  rainbow: 'Rainbow magic: fills nests, boosts laying & gathering, or supercharges clicks.',
  roosterEgg: 'Keep it warm — it hatches a cockerel that makes your next rooster 25% cheaper.',
  broody: '',
  broodEgg: "A broody hen's hatch. Click to collect her reward.",
};

function tooltipHtml(t: HitTarget): string | null {
  const s = game.state;
  const c = game.ctx();
  const flock = game.viewFlock();
  if (t.kind === 'event') {
    const e = s.events.find((x) => x.id === t.id);
    if (!e) return null;
    const left = Number.isFinite(e.expiresAt) && !e.incubateUntil ? ` <span style="opacity:.7">(${formatDuration(e.expiresAt - s.time)} left)</span>` : '';
    const reward = e.kind === 'broodEgg' ? `<br>Reward: <b>${formatMoney(e.reward ?? 0)}</b>` : '';
    return `<b>${e.bloom ? 'Bloom egg' : e.kind.replace(/([A-Z])/g, ' $1').replace(/^./, (x) => x.toUpperCase())}</b>${left}<br>${e.bloom ? 'A Mille Fleur bloom — collects every egg at double value.' : EVENT_HELP[e.kind]}${reward}`;
  }
  if (t.kind === 'critter') {
    const cr = s.critters.find((x) => x.id === t.id) as Critter | undefined;
    if (!cr) return null;
    if (cr.kind === 'butterfly') return '<b>Butterfly</b><br>Click for 1.5× laying speed (10s).';
    const label = cr.kind === 'megaWorm' ? `Mega worm (${cr.hp} clicks)` : cr.kind === 'stormEgg' ? 'Storm egg' : cr.kind[0]!.toUpperCase() + cr.kind.slice(1);
    return `<b>${label}</b><br>Click for <b>${formatMoney(critterReward(s, c, cr))}</b>`;
  }
  if (t.kind === 'rooster' && flock.rooster) {
    const r = flock.rooster;
    const rb = ROOSTER_BREEDS[r.breed];
    const cs = crowStats(s, c, r.breed);
    const ready = s.time >= r.crowReadyAt;
    return `<b>${esc(r.name)}</b> — ${rb.name} rooster<br>${ROOSTER_PASSIVES[rb.passive].description(rb.aura)}<br>Crow: ×${cs.power.toFixed(1)} laying for ${Math.round(cs.durationMs / 1000)}s, every ${formatDuration(cs.cooldownMs)}.<br>${ready ? '<b>Ready — click to crow!</b>' : `Next crow in ${formatDuration(r.crowReadyAt - s.time)}`}`;
  }
  if (t.kind === 'box') {
    const box = flock.boxes[t.index];
    if (!box) return t.index === flock.boxes.length ? `<b>Empty lot</b><br>Click to build a nesting box for <b>${formatMoney(nestBoxCost(s, flock.id))}</b>.` : null;
    if (!box.hen) return '<b>Empty nest</b><br>Click to choose a hen for it.';
    const hen = box.hen;
    const breed = HEN_BREEDS[hen.breed];
    const st = henStats(s, c, flock, hen);
    const lines = [
      `<b>${breed.name}</b> <span style="opacity:.7">${breed.tier}</span>`,
      hen.broodySince !== null ? '🪺 Broody — not laying' : `${st.layRate.toFixed(2)} eggs/s × ${formatRate(st.eggValue)} = <b>${formatRate(st.layRate * st.eggValue)}/s</b>`,
      `Nest: ${box.eggs}/${nestCapacity(box)} eggs · click value ×${st.clickMult.toFixed(2)}`,
    ];
    if (isFirstOfKind(flock, hen)) lines.push('★ First of her breed in this flock: eggs worth +75%');
    if (breed.special) lines.push(`<b>${HEN_SPECIALS[breed.special].name}:</b> ${HEN_SPECIALS[breed.special].description}`);
    if (hen.traits.length) lines.push(`Traits: ${hen.traits.map((tr) => TRAITS[tr].name).join(', ')}`);
    lines.push('<span style="opacity:.6">Click to collect · right-click to release</span>');
    return lines.join('<br>');
  }
  return null;
}
